import React, { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { marked } from 'marked';
import { MessageCircle, Send, X } from 'lucide-react';
import { useHintEditor } from '../context/HintEditorContext';
import { problemIdFromPath, requestHint } from '../utils/hintChat';
import './HintChatbot.css';

const WELCOME_TEXT =
  "Ask me anything about the idea — I'll teach and nudge, but I won't write the solution for you.";

marked.setOptions({ gfm: true, breaks: true });

function renderAssistantHtml(text) {
  return marked.parse(text, { async: false });
}

const HintChatbot = ({ user }) => {
  const location = useLocation();
  const { activeWorkspace, editorCode, simulatorCode, simulatorState } = useHintEditor();
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);
  const [messages, setMessages] = useState([{ role: 'assistant', text: WELCOME_TEXT }]);
  const listRef = useRef(null);
  const inputRef = useRef(null);

  const problemId = problemIdFromPath(location.pathname);

  useEffect(() => {
    setMessages([{ role: 'assistant', text: WELCOME_TEXT }]);
    setDraft('');
  }, [problemId]);

  useEffect(() => {
    if (!listRef.current) {
      return;
    }
    listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [messages, open]);

  useEffect(() => {
    if (open) {
      inputRef.current?.focus();
    }
  }, [open]);

  if (!user) {
    return null;
  }

  const sendHint = async (event) => {
    event.preventDefault();
    const message = draft.trim();
    if (!message || busy) {
      return;
    }

    setDraft('');
    setMessages((prev) => [...prev, { role: 'user', text: message }]);
    setBusy(true);

    const history = messages
      .slice(1)
      .filter((item) => !item.error && (item.role === 'user' || item.role === 'assistant'))
      .slice(-12)
      .map((item) => ({ role: item.role, content: item.text }));

    try {
      const hint = await requestHint({
        message,
        problemId,
        workspaceMode: problemId ? activeWorkspace : undefined,
        editorCode: problemId ? editorCode : undefined,
        simulatorCode: problemId ? simulatorCode : undefined,
        simulatorState: problemId ? simulatorState : undefined,
        history,
      });
      setMessages((prev) => [...prev, { role: 'assistant', text: hint }]);
    } catch (error) {
      setMessages((prev) => [
        ...prev,
        {
          role: 'assistant',
          text: error?.message || 'Could not get a hint right now.',
          error: true,
        },
      ]);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="hint-chatbot">
      {open && (
        <section className="hint-chatbot__panel" aria-label="Hint coach">
          <header className="hint-chatbot__header">
            <div className="hint-chatbot__heading">
              <p className="hint-chatbot__title">Hint coach</p>
              <p className="hint-chatbot__subtitle">
                I'll point you in the right direction — I just won't write the answer.
              </p>
            </div>
            <button
              type="button"
              className="hint-chatbot__icon-btn"
              onClick={() => setOpen(false)}
              aria-label="Close hint coach"
            >
              <X size={16} strokeWidth={2.25} />
            </button>
          </header>

          <div className="hint-chatbot__messages" ref={listRef} role="log" aria-live="polite">
            {messages.map((item, index) => (
              <div
                key={`${item.role}-${index}`}
                className={`hint-chatbot__bubble hint-chatbot__bubble--${item.role}${
                  item.error ? ' hint-chatbot__bubble--error' : ''
                }`}
              >
                {item.role === 'assistant' && !item.error ? (
                  <div
                    className="hint-chatbot__markdown"
                    dangerouslySetInnerHTML={{ __html: renderAssistantHtml(item.text) }}
                  />
                ) : (
                  item.text
                )}
              </div>
            ))}
            {busy && (
              <div className="hint-chatbot__bubble hint-chatbot__bubble--assistant hint-chatbot__bubble--pending">
                Thinking of a hint…
              </div>
            )}
          </div>

          <form className="hint-chatbot__composer" onSubmit={sendHint}>
            <label className="visually-hidden" htmlFor="hint-chatbot-input">
              Ask for a hint
            </label>
            <textarea
              id="hint-chatbot-input"
              ref={inputRef}
              className="hint-chatbot__input"
              rows={2}
              value={draft}
              disabled={busy}
              placeholder={problemId ? `Hint for problem ${problemId}…` : 'Ask for a conceptual hint…'}
              onChange={(event) => setDraft(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter' && !event.shiftKey) {
                  event.preventDefault();
                  sendHint(event);
                }
              }}
            />
            <button
              type="submit"
              className="hint-chatbot__send"
              disabled={busy || !draft.trim()}
              aria-label="Send hint request"
            >
              <Send size={16} strokeWidth={2.25} />
            </button>
          </form>
        </section>
      )}

      <button
        type="button"
        className={`hint-chatbot__launcher${open ? ' hint-chatbot__launcher--open' : ''}`}
        onClick={() => setOpen((prev) => !prev)}
        aria-expanded={open}
        aria-label={open ? 'Close hint coach' : 'Open hint coach'}
      >
        {open ? <X size={22} strokeWidth={2.1} /> : <MessageCircle size={22} strokeWidth={2.1} />}
      </button>
    </div>
  );
};

export default HintChatbot;
